// Libraries
import enUS from 'antd-mobile/es/locales/en-US';
import viVN from 'antd-mobile/es/locales/vi-VN';
import jaJP from 'antd-mobile/es/locales/ja-JP';

// Constants
import { LANGUAGE, MESSAGE_TYPE } from '@app/constants/common';

// Utils
import { isArrayAndNotEmpty } from '@app/utils/arrayUtil';

/**
 * Get locale by language code
 *
 * @param {string} languageCode - The language code like vi (Vietnamese), en (English), ja (Japanese), ...
 * @returns {import('antd/lib/locale').Locale} Return the locale base on language code
 */
function getLocaleByLanguageCode(languageCode) {
  switch (languageCode) {
    case LANGUAGE.ENGLISH:
      return enUS;
    case LANGUAGE.VIETNAMESE:
      return viVN;
    case LANGUAGE.JAPANESE:
      return jaJP;
    default:
      break;
  }

  return viVN;
}

/**
 * Localize rule
 *
 * @param {Array} rule - The rule array
 * @param {import('i18next').TFunction} t - The translation function
 * @returns {Array} Return the rule with message is translated
 */
function localizeRule(rule, t) {
  return rule.map((item) => ({
    ...item,
    message: t(item.message),
  }));
}

/**
 * Get the location/url from path name
 *
 * @param {string} pathName - The path name
 * @param {Array} paths - The paths array
 * @returns
 */
function getLocationFromPath(pathName, paths) {
  if (paths.length === 0) {
    return '/';
  }

  if (paths.indexOf('home') > -1) {
    return '/' + paths.slice(1, paths.indexOf(pathName) + 1).join('/');
  }

  return '/' + paths.slice(0, paths.indexOf(pathName) + 1).join('/');
}

/**
 * Generate UUID
 */
function generateUUID() {
  let date1 = new Date().getTime();
  let date2 =
    (typeof performance !== 'undefined' &&
      performance.now &&
      performance.now() * 1000) ||
    0;
  return 'xxxx9xxx-xxxx-xyxx-yxxx-1xxxxxxxxxxx'.replace(/[xy]/g, (x) => {
    let rd = Math.random() * 16;
    if (date1 > 0) {
      rd = (date1 + rd) % 16 | 0;
      date1 = Math.floor(date1 / 16);
    } else {
      rd = (date2 + rd) % 16 | 0;
      date2 = Math.floor(date2 / 16);
    }

    return (x === 'x' ? rd : (rd & 0x3) | 0x8).toString(16);
  });
}

/**
 * Compare 2 value
 *
 * @param {string | number} a - Value a
 * @param {string | number} b - Value b
 * @returns {number}
 */
function compare(a, b) {
  return a > b ? 1 : a < b ? -1 : 0;
}

/**
 * Add index attribute in array of object.
 *
 * @param {Array} arr - array of object
 * @returns {Array}
 */
function addIndexToArrayOfObject(arr) {
  return arr.map((record, index) => ({ index: index + 1, ...record }));
}

/**
 * Get array filters by field
 *
 * @param {Array} arr
 * @param {string} field - field of table
 * @returns {Array}
 */
function getArrayFiltersByField(data, field) {
  return data
    .map((value) => value[field])
    .filter((v, i, a) => a.indexOf(v) === i)
    .map((value) => ({ text: value, value: value }));
}

/**
 * Add random key attribute in each item of array
 *
 * @param {Array} arr
 * @param {string} field - field of table
 * @returns {Array}
 */
const addRandomKey = (arr) => {
  return arr.map((record) => ({
    key: (Math.random() * 10000000).toFixed(0),
    ...record,
  }));
};

/**
 *
 * @param {Array} data input data
 * @param {String} label select label
 * @param {String} value select value
 * @returns select option
 */
export const convertToSelectOption = (data, label, value) => {
  if (!isArrayAndNotEmpty(data)) return [];

  return data?.map((x, i) => ({
    label: x[label],
    value: x[value],
    key: i,
  }));
};

/**
 * Init form field value
 *
 * @param {import('antd').FormInstance} form - The form instance
 * @param {Array<OptionValue>} optionValues - The array option values
 * @param {string} fieldName - The field name
 * @param {string} [reportParam] - The report param
 * @param {Function} [addReportParam] - The add report param function
 * @param {string|number|null} [value] - The value want to set
 * @param {boolean|null} [isMultiple] - The multiple mode flag
 */
function initFormFieldValue(
  form,
  optionValues,
  fieldName,
  reportParam,
  addReportParam,
  value,
  isMultiple
) {
  const valueToSet =
    optionValues.length > 0
      ? !!isMultiple
        ? [value || optionValues[0].value]
        : value || optionValues[0].value
      : null;

  if (!!valueToSet || valueToSet === 0) {
    form.setFieldValue(fieldName, valueToSet);
  }

  if (!!reportParam && (!!valueToSet || valueToSet === 0)) {
    addReportParam(reportParam, valueToSet);
  }
}

/**
 * Init form fields value
 *
 * @param {import('antd').FormInstance} form - The form instance
 * @param {Array<Field>} fields - The array fields
 * @param {Function} [addReportParam] - The add report param function
 */
function initFormFieldsValue(form, fields, addReportParam) {
  fields.forEach((field) => {
    initFormFieldValue(
      form,
      field.optionValues,
      field.name,
      field.reportParam,
      addReportParam || field.addReportParam,
      null,
      field.isMultiple
    );
  });
}

/**
 * Localize form field attributes
 *
 * @param {Object} field - The object field
 * @param {import('i18next').TFunction} t - The translation function
 * @example // returns
 *          //  <CustomSelect
 *          //      name='shift'
 *          //      label={t('forms.common.shift')}
 *          //      placeholder={t('common.place-holder.all')}
 *          //      rules=[{
 *          //          message: t('common.message.required)
 *          //      }]
 *          //  />
 * <CustomSelect
 *     {...localizeFormFieldAttributes(
 *         {
 *             name: 'shift',
 *             label: 'forms.common.shift',
 *             placeholder: 'common.place-holder.all',
 *             rules: [{
 *                 message: 'common.message.required'
 *             }]
 *         },
 *         t
 *     )}
 * />
 * @returns {Array} Return the rule with message is translated
 */
function localizeFormFieldAttributes(field, t) {
  const result = { ...field };
  Object.keys(field).forEach((key) => {
    if (key === 'label' || key === 'placeholder' || key === 'children') {
      result[key] = t(field[key]);
    }

    if (key === 'rules') {
      result[key] = result[key].map((item) => ({
        ...item,
        message: t(item.message, { fieldName: result.label.toLowerCase() }),
      }));
    }

    if (key === 'options') {
      result[key] = result[key].map((item) => ({
        ...item,
        label: t(item.label),
      }));
    }
  });

  return result;
}

/**
 * Show message alert in screen
 *
 * @param {import('antd/es/message/interface').MessageInstance} msg - The message instance
 * @param {string} message - The message string
 * @param {number} type - The message type
 */
function showMessage(
  msg,
  message,
  type = MESSAGE_TYPE.SUCCESS,
  onClose = () => {}
) {
  if (!msg) {
    return;
  }

  switch (type) {
    case MESSAGE_TYPE.ERROR:
      msg.error(message, undefined, onClose);
      break;
    case MESSAGE_TYPE.SUCCESS:
      msg.success(message, undefined, onClose);
      break;
    case MESSAGE_TYPE.WARNING:
      msg.warning(message, undefined, onClose);
      break;
    case MESSAGE_TYPE.INFO:
      msg.info(message, undefined, onClose);
      break;
    default:
      break;
  }
}

function removeVietnameseTones(str) {
  str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a');
  str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e');
  str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i');
  str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o');
  str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u');
  str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y');
  str = str.replace(/đ/g, 'd');
  str = str.replace(/À|Á|Ạ|Ả|Ã|Â|Ầ|Ấ|Ậ|Ẩ|Ẫ|Ă|Ằ|Ắ|Ặ|Ẳ|Ẵ/g, 'A');
  str = str.replace(/È|É|Ẹ|Ẻ|Ẽ|Ê|Ề|Ế|Ệ|Ể|Ễ/g, 'E');
  str = str.replace(/Ì|Í|Ị|Ỉ|Ĩ/g, 'I');
  str = str.replace(/Ò|Ó|Ọ|Ỏ|Õ|Ô|Ồ|Ố|Ộ|Ổ|Ỗ|Ơ|Ờ|Ớ|Ợ|Ở|Ỡ/g, 'O');
  str = str.replace(/Ù|Ú|Ụ|Ủ|Ũ|Ư|Ừ|Ứ|Ự|Ử|Ữ/g, 'U');
  str = str.replace(/Ỳ|Ý|Ỵ|Ỷ|Ỹ/g, 'Y');
  str = str.replace(/Đ/g, 'D');
  // Some system encode vietnamese combining accent as individual utf-8 characters
  // Một vài bộ encode coi các dấu mũ, dấu chữ như một kí tự riêng biệt nên thêm hai dòng này
  str = str.replace(/\u0300|\u0301|\u0303|\u0309|\u0323/g, ''); // ̀ ́ ̃ ̉ ̣  huyền, sắc, ngã, hỏi, nặng
  str = str.replace(/\u02C6|\u0306|\u031B/g, ''); // ˆ ̆ ̛  Â, Ê, Ă, Ơ, Ư
  // Remove extra spaces
  // Bỏ các khoảng trắng liền nhau
  str = str.replace(/ + /g, ' ');
  str = str.trim();
  // Remove punctuations
  // Bỏ dấu câu, kí tự đặc biệt
  str = str.replace(
    /!|@|%|\^|\*|\(|\)|\+|=|<|>|\?|\/|,|\.|:|;|'|"|&|#|\[|\]|~|\$|_|`|-|{|}|\||\\/g,
    ' '
  );
  return str;
}

/**
 * Get modal config
 *
 * @param {*} ModalContext - Modal context
 * @param {string} title - The title
 * @param {string} content - The content of modal
 * @returns {Object} Modal config
 */
function getModalConfig(ModalContext, title, content) {
  return {
    title,
    content: (
      <>
        <ModalContext.Consumer>{() => content}</ModalContext.Consumer>
      </>
    ),
  };
}

/**
 * Get modal config with footer customize
 *
 * @param {*} ModalContext
 * @param {string} title - The title
 * @param {string} content - The content of modal
 * @param {Function} onOk - The action when press ok button
 * @param {Function} onCancel - The action when press cancel button
 * @param {import('react').ReactNode} footer - The footer to customize
 * @returns {Object} Modal config
 */
function getModalConfigWithFooterCustomize(
  ModalContext,
  title,
  content,
  onOk = () => {},
  onCancel = () => {},
  footer = (_, { OkBtn, CancelBtn }) => (
    <>
      <CancelBtn />
      <OkBtn />
    </>
  )
) {
  return {
    title,
    content: (
      <>
        <ModalContext.Consumer>{() => content}</ModalContext.Consumer>
      </>
    ),
    onOk,
    onCancel,
    footer,
  };
}

/**
 * Print pdf with blob
 *
 * @param {Blob} resBlob - Blob data
 */
function printPdf(resBlob) {
  const blob = new Blob([resBlob], {
    type: 'application/pdf',
  });
  const blobUrl = URL.createObjectURL(blob);
  const iframeToPrint = document.createElement('iframe');
  document.body.appendChild(iframeToPrint);
  iframeToPrint.style.display = 'none';
  iframeToPrint.src = blobUrl;

  iframeToPrint.onload = () => {
    setTimeout(() => {
      iframeToPrint.focus();
      iframeToPrint.contentWindow.print();
    }, 1);
  };
}

/**
 * Remove Vietnamese ascent
 *
 * @param {string} str - The text to remove
 * @returns {string} The result text
 */
function removeVietnameseAscent(str) {
  if (str === null || str === undefined) return str;
  str = str.toLowerCase();
  str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a');
  str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e');
  str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i');
  str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o');
  str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u');
  str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y');
  str = str.replace(/đ/g, 'd');
  return str;
}

/**
 * Check fullname is valid or not
 *
 * @param {string} string
 * @returns {bool}
 */
function isValidFullName(string) {
  var re = /^[a-zA-Z0-9 ]{2,50}$/g; // regex here
  return re.test(removeVietnameseAscent(string));
}

/**
 * Parse json response data to array
 *
 * @param {string} apiResponse - Json response
 * @returns {Array} Return array
 */
function parseJsonResponse(apiResponse) {
  if (apiResponse && apiResponse.trim() !== '') {
    try {
      const parsedData = JSON.parse(apiResponse);
      const dataArray = Array.isArray(parsedData) ? parsedData : [];
      const dataWkey = dataArray.map((x, i) => ({
        ...x,
        key: i,
        stt: i + 1,
      }));
      return dataWkey;
    } catch (error) {
      console.log('Không thể phân tích dữ liệu JSON', error);
    }
  } else {
    return [];
  }
}

async function getBase64(file) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result?.split(',')[1]);
    reader.onerror = (error) => reject(error);
  });
}

function hexToArgbInt(hex) {
  hex = hex.replace('#', '');
  if (hex.length === 6) {
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    let a = 255;

    let argb = (a << 24) | (r << 16) | (g << 8) | b;
    if (argb >= 0x80000000) {
      argb = argb - 0x100000000;
    }
    return argb;
  }
  return null;
}

export {
  getLocaleByLanguageCode,
  localizeRule,
  generateUUID,
  compare,
  addIndexToArrayOfObject,
  getArrayFiltersByField,
  addRandomKey,
  initFormFieldValue,
  initFormFieldsValue,
  localizeFormFieldAttributes,
  showMessage,
  removeVietnameseTones,
  getModalConfig,
  printPdf,
  isValidFullName,
  getModalConfigWithFooterCustomize,
  parseJsonResponse,
  getBase64,
  hexToArgbInt,
};
